import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import logo from "../../images/cq-small-logo.png";
import { users } from "../../services/api/models";
import {
  handleErrorMessage,
  roleData,
  getValidationSchema,
} from "../../validation/profileData.validation";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { ToastContainer, toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Step, Stepper } from "react-form-stepper";
import { Button } from "react-bootstrap";
import {
  AWARENESS_CHANNELS,
  EXPERIENCE_LEVELS,
  FIELDS_INTEREST,
  JOB_STATUS,
  POSITIONS,
  ROLES,
  STUDY_YEARS,
} from "../../utils/constants";
import { useAuthHelper } from "../../hooks/useAuthHelper";

function ProfileData() {
  const navigate = useNavigate();
  const { user, loginWithRedirect, logout } = useAuth0();
  const { getSafeAccessToken } = useAuthHelper();
  const [clickLoading, setClickLoading] = useState(false);
  const [openInterestFields, setOpenInterestFields] = useState(false);
  const [openAwarenessChannels, setOpenAwarenessChannels] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isNumberExists, setIsNumberExists] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [positionDropdownOpen, setPositionDropdownOpen] = useState(false);
  const [studyYearDropdownOpen, setStudyYearDropdownOpen] = useState(false);
  const [jobStatusDropdownOpen, setJobStatusDropdownOpen] = useState(false);
  const [experienceYearsDropdownOpen, setExperienceYearsDropdownOpen] =
    useState(false);

  const location = useLocation();

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(getValidationSchema(true)),
    defaultValues: {
      role: "contestant",
      studyYear: "",
      position: "",
      jobStatus: "", // Ensure this is initialized
      interestFields: [],
      where_you_know_us: [],
      otherAwarenessChannel: "",
    },
    mode: "onChange",
  });

  const role = watch("role");
  const position = watch("position");
  const studyYear = watch("studyYear");
  const whereYouKnowUs = watch("where_you_know_us");
  const jobStatus = watch("jobStatus");
  const isOtherSelected = whereYouKnowUs?.includes("Other");

  const onSubmit = async (formData) => {
    //console.log("Form data being submitted:", formData); // Debug log
    setClickLoading(true);

    // Combine where_you_know_us and otherAwarenessChannel
    let finalWhereYouKnowUs = [
      ...formData.where_you_know_us,
      ...(formData.otherAwarenessChannel
        ? [formData.otherAwarenessChannel]
        : []),
    ];
    finalWhereYouKnowUs = finalWhereYouKnowUs.filter(
      (value) => value !== "Other"
    );

    // Prepare the data to be submitted - ensure jobStatus is included
    const updatedFormData = {
      ...formData,
      where_you_know_us: finalWhereYouKnowUs,
      // Explicitly ensure jobStatus is included
      jobStatus: formData.jobStatus || "",
    };

    //console.log("Updated form data:", updatedFormData); // Debug log

    const processedRoleData = roleData(updatedFormData);
    //console.log("Processed role data:", processedRoleData); // Debug log

    const requestBody = {
      ...processedRoleData,
      avatar: user.picture,
      auth0Id: user.sub,
      email: user.email,
    };

    //console.log("Final request body:", requestBody); // Debug log

    try {
      const accessToken = await getSafeAccessToken();

      // If token is null, redirect is happening
      if (!accessToken) {
        setClickLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${accessToken}` };
      const userFilter = { auth0Id: user.sub };

      const { data } = await users.usersGet(1, 0, userFilter, { headers });
      // check if user has phone number and navigate to quest-list if exists
      if (data.data.length > 0 && data.data[0].whatsAppPhoneNumber) {
        setIsNumberExists(true);
      }
      if (data.data.length === 0) {
        await users.usersPost(requestBody, { headers });
      } else {
        await users.usersPatch(requestBody, userFilter, { headers });
      }

      toast.success("Success Sign Up");
      setTimeout(
        () =>
          loginWithRedirect({
            appState: { returnTo: location.pathname },
          }),
        2000
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      console.error("Error in updating user data", err);
    } finally {
      setClickLoading(false);
    }
  };

  const handleContinueToStep2 = async (e) => {
    e.preventDefault();
    const isValid = await trigger([
      "name",
      "whatsAppPhoneNumber",
      "role",
      "nickname",
      "position",
      "githubHandle",
      "behanceProfileLink",
      "terms",
    ]);
    if (isValid) setCurrentStep(1);
  };

  const handleSkipStep2 = (e) => {
    e.preventDefault();
    handleSubmit((formData) => onSubmit({ ...formData, skipStep2: true }))();
  };

  const onLogout = () => {
    logout({
      logoutParams: {
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
        returnTo: process.env.REACT_APP_BASE_URL,
      },
    });
  };

  const handleWhereYouKnowUsChange = (event) => {
    event.stopPropagation(); // Prevent dropdown from closing
    const value = event.target.value;
    const currentValues = watch("where_you_know_us") || [];
    let newWhereYouKnowUs;

    if (event.target.checked) {
      newWhereYouKnowUs = [...currentValues, value];
    } else {
      newWhereYouKnowUs = currentValues.filter((field) => field !== value);
    }

    setValue("where_you_know_us", newWhereYouKnowUs); // Update form value
  };

  // Handle click outside for dropdowns
  const dropdownRef = useRef(null);
  const awarenessChannelsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside Interest Fields dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if click is not on the dropdown trigger
        const interestFieldsTrigger = document.getElementById("interestFields");
        if (
          !interestFieldsTrigger ||
          !interestFieldsTrigger.contains(event.target)
        ) {
          setOpenInterestFields(false);
        }
      }

      // Check if click is outside Where You Know Us dropdown
      if (
        awarenessChannelsRef.current &&
        !awarenessChannelsRef.current.contains(event.target)
      ) {
        // Check if click is not on the dropdown trigger
        const whereYouKnowUsTrigger = document.getElementById("whereYouKnowUs");
        if (
          !whereYouKnowUsTrigger ||
          !whereYouKnowUsTrigger.contains(event.target)
        ) {
          setOpenAwarenessChannels(false);
        }
      }

      // Close all other dropdowns when clicking outside
      if (
        !event.target.closest(".form-select") &&
        !event.target.closest(".dropdown-options")
      ) {
        setDropdownOpen(false);
        setPositionDropdownOpen(false);
        setStudyYearDropdownOpen(false);
        setJobStatusDropdownOpen(false);
        setExperienceYearsDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (user.role && isNumberExists) {
    loginWithRedirect();
    navigate("/quest-list");
    return null;
  }

  return (
    <div className="authincation d-flex flex-column flex-lg-row flex-column-fluid">
      <div className="container flex-row-fluid d-flex flex-column justify-content-center position-relative overflow-hidden p-7 mx-auto">
        <div className="d-flex justify-content-center h-100 align-items-center">
          <div
            className="authincation-content style-2"
            style={{ maxWidth: "47rem" }}
          >
            <div className="row no-gutters">
              <div className="col-xl-12 tab-content position-relative">
                <div
                  id="sign-in"
                  className="auth-form form-validation bg-white position-relative"
                  style={{ borderRadius: "2%", fontSize: "11px" }}
                >
                  <div
                    style={{ height: "7rem" }}
                    className="mb-3 d-flex justify-content-between align-items-start"
                  >
                    <Button
                      variant="btn-rounded"
                      className="p-0 fs-4 position-relative"
                      onClick={() =>
                        setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev))
                      }
                    >
                      <i
                        className={`fa-solid fa-chevron-left cursor-pointer ${
                          currentStep <= 0 ? "text-white" : "text-primary"
                        }`}
                      ></i>
                    </Button>
                    <div className="h-100 d-flex flex-column align-items-center text-center align-self-center">
                      <img src={logo} alt="logo" className="h-100" />
                      <h2 className="text-primary mt-4">Sign Up</h2>
                    </div>
                    <button
                      type="button"
                      onClick={onLogout}
                      className="btn-close"
                      style={{ height: "2em", fontSize: "18px" }}
                    ></button>
                  </div>

                  <form className="form-validate form-wizard">
                    <div
                      className="d-flex gap-4 justify-content-center"
                      style={{ margin: "80px 0 20px 0" }}
                    >
                      <div
                        className="bg-primary"
                        style={{
                          height: "8px",
                          width: "120px",
                          borderRadius: "100px",
                        }}
                      ></div>
                      <div
                        className={`${
                          currentStep === 1 ? "bg-primary" : "bg-light"
                        }`}
                        style={{
                          height: "8px",
                          width: "120px",
                          borderRadius: "100px",
                        }}
                      ></div>
                    </div>

                    {currentStep === 0 && (
                      <>
                        {/* Step 1 Fields */}
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label
                              htmlFor="name"
                              className="form-label font-w600"
                            >
                              Name{" "}
                              <span className="text-danger scale5 ms-2">*</span>
                            </label>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              className="form-control mb-1"
                              placeholder="Please Enter Your Name (e.g. John Doe)"
                              autoComplete="name"
                              {...register("name")}
                            />
                            {errors.name && (
                              <div className="text-danger fs-12">
                                {errors.name.message}
                              </div>
                            )}
                          </div>
                          <div className="form-group mb-3 col-md-6">
                            <label
                              htmlFor="whatsAppPhoneNumber"
                              className="form-label font-w600"
                            >
                              Whatsapp Phone Number{" "}
                              <span className="text-danger scale5 ms-2">*</span>
                            </label>
                            <input
                              id="whatsAppPhoneNumber"
                              name="whatsAppPhoneNumber"
                              type="tel"
                              className="form-control mb-1"
                              placeholder="Type a Unique Phone Number (e.g. +1234567890)"
                              autoComplete="tel"
                              {...register("whatsAppPhoneNumber")}
                            />
                            {errors.whatsAppPhoneNumber && (
                              <div className="text-danger fs-12">
                                {errors.whatsAppPhoneNumber.message}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Role Selection */}
                        <div className="form-group mb-3">
                          <label
                            htmlFor="role"
                            className="form-label font-w600"
                          >
                            Role{" "}
                            <span className="text-danger scale5 ms-2">*</span>
                          </label>

                          {/* Hidden input for React Hook Form */}
                          <input
                            type="hidden"
                            {...register("role")}
                            value={watch("role") || ""}
                          />

                          {/* Custom dropdown that looks like form-select */}
                          <div
                            className={`form-select form-control solid mb-1 ${
                              dropdownOpen ? "dropdown-open" : ""
                            }`}
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            style={{
                              cursor: "pointer",
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                            }}
                          >
                            <div
                              className="dropdown-selected"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                width: "50%",
                                height: "100%",
                              }}
                            >
                              {watch("role") || "Choose..."}
                            </div>

                            {dropdownOpen && (
                              <ul
                                className="dropdown-options"
                                style={{
                                  position: "absolute",
                                  top: "calc(100% + 8px)",
                                  left: "2.5%",
                                  right: "0",
                                  width: "95%",
                                  zIndex: 1000,
                                  backgroundColor: "#fff",
                                  border: "1px solid #e6e6e6",
                                  borderRadius: "0.75rem",
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  listStyle: "none",
                                  padding: "0",
                                  margin: "0",
                                }}
                              >
                                {ROLES.map((role, index) => (
                                  <li
                                    key={index}
                                    className="dropdown-option"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setValue("role", role);
                                      setDropdownOpen(false);
                                    }}
                                    style={{
                                      padding: "0.75rem 1rem",
                                      cursor: "pointer",
                                      fontSize: "0.875rem",
                                      color: "#888888",
                                      margin: "0",
                                      display: "block",
                                      width: "100%",
                                      boxSizing: "border-box",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor =
                                        "#F93A0B";
                                      e.target.style.color = "#fff";
                                      e.target.style.padding = "0.75rem 1rem";
                                      e.target.style.margin = "0";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor =
                                        "transparent";
                                      e.target.style.color = "#888888";
                                      e.target.style.padding = "0.75rem 1rem";
                                      e.target.style.margin = "0";
                                    }}
                                  >
                                    {role}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          {errors.role && (
                            <div className="text-danger fs-12">
                              {errors.role.message}
                            </div>
                          )}
                        </div>

                        {/* Conditional Fields Based on Role */}
                        {role && role !== "client" && (
                          <>
                            <div className="form-group mb-3">
                              <label
                                htmlFor="nickname"
                                className="form-label font-w600"
                              >
                                Nickname{" "}
                                <span className="text-danger scale5 ms-2">
                                  *
                                </span>
                              </label>
                              <input
                                id="nickname"
                                name="nickname"
                                type="text"
                                className="form-control mb-1"
                                placeholder="Enter a Unique Nickname (e.g. johndoe123)"
                                autoComplete="nickname"
                                {...register("nickname")}
                              />
                              {errors.nickname && (
                                <div className="text-danger fs-12">
                                  {errors.nickname.message}
                                </div>
                              )}
                            </div>

                            <div className="row">
                              <div className="form-group mb-3 col-md-6">
                                <label
                                  htmlFor="position"
                                  className="form-label font-w600"
                                >
                                  Designer OR Developer{" "}
                                  <span className="text-danger scale5 ms-2">
                                    *
                                  </span>
                                </label>

                                {/* Hidden input for React Hook Form */}
                                <input
                                  type="hidden"
                                  {...register("position")}
                                  value={watch("position") || ""}
                                />

                                {/* Custom dropdown that looks like form-select */}
                                <div
                                  className={`form-select form-control solid mb-1 ${
                                    positionDropdownOpen ? "dropdown-open" : ""
                                  }`}
                                  onClick={() =>
                                    setPositionDropdownOpen(
                                      !positionDropdownOpen
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <div
                                    className="dropdown-selected"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "50%",
                                      height: "100%",
                                    }}
                                  >
                                    {watch("position") || "Choose..."}
                                  </div>

                                  {positionDropdownOpen && (
                                    <ul
                                      className="dropdown-options"
                                      style={{
                                        position: "absolute",
                                        top: "calc(100% + 8px)",
                                        left: "2.5%",
                                        right: "0",
                                        width: "95%",
                                        zIndex: 1000,
                                        backgroundColor: "#fff",
                                        border: "1px solid #e6e6e6",
                                        borderRadius: "0.75rem",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        listStyle: "none",
                                        padding: "0",
                                        margin: "0",
                                      }}
                                    >
                                      {POSITIONS.map((position, index) => (
                                        <li
                                          key={index}
                                          className="dropdown-option"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setValue("position", position);
                                            setPositionDropdownOpen(false);
                                          }}
                                          style={{
                                            padding: "0.75rem 1rem",
                                            cursor: "pointer",
                                            fontSize: "0.875rem",
                                            color: "#888888",
                                            margin: "0",
                                            display: "block",
                                            width: "100%",
                                            boxSizing: "border-box",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor =
                                              "#F93A0B";
                                            e.target.style.color = "#fff";
                                            e.target.style.padding =
                                              "0.75rem 1rem";
                                            e.target.style.margin = "0";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor =
                                              "transparent";
                                            e.target.style.color = "#888888";
                                            e.target.style.padding =
                                              "0.75rem 1rem";
                                            e.target.style.margin = "0";
                                          }}
                                        >
                                          {position}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>

                                {errors.position && (
                                  <div className="text-danger fs-12">
                                    {errors.position.message}
                                  </div>
                                )}
                              </div>

                              {position === "Developer" && (
                                <div className="form-group mb-3 col-md-6">
                                  <label
                                    htmlFor="githubHandle"
                                    className="form-label font-w600"
                                  >
                                    Github Handle{" "}
                                    <span className="text-danger scale5 ms-2">
                                      *
                                    </span>
                                  </label>
                                  <input
                                    id="githubHandle"
                                    name="githubHandle"
                                    type="url"
                                    className="form-control mb-1"
                                    placeholder="Enter a Unique GitHub username (e.g. john-doe)"
                                    autoComplete="url"
                                    {...register("githubHandle")}
                                  />
                                  {errors.githubHandle && (
                                    <div className="text-danger fs-12">
                                      {errors.githubHandle.message}
                                    </div>
                                  )}
                                </div>
                              )}

                              {position === "Designer" && (
                                <div className="form-group mb-3 col-md-6">
                                  <label
                                    htmlFor="behanceProfileLink"
                                    className="form-label font-w600"
                                  >
                                    Behance{" "}
                                    <span className="text-danger scale5 ms-2">
                                      *
                                    </span>
                                  </label>
                                  <input
                                    id="behanceProfileLink"
                                    name="behanceProfileLink"
                                    type="url"
                                    className="form-control mb-1"
                                    placeholder="Enter a Unique Behance profile URL (e.g. bjohn-doe)"
                                    autoComplete="url"
                                    {...register("behanceProfileLink")}
                                  />
                                  {errors.behanceProfileLink && (
                                    <div className="text-danger fs-12">
                                      {errors.behanceProfileLink.message}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Terms and Conditions */}
                        <div className="form-check mx-1">
                          <input
                            type="checkbox"
                            id="terms"
                            name="terms"
                            className="form-check-input"
                            {...register("terms")}
                          />
                          <label htmlFor="terms" className="form-check-label">
                            By signing up, you agree to{" "}
                            <a
                              href="https://code-quests.com/privacy-policy-2/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary"
                            >
                              the terms of service
                            </a>{" "}
                            of code-quests.com
                          </label>
                          {errors.terms && (
                            <div className="text-danger fs-12">
                              {errors.terms.message}
                            </div>
                          )}
                        </div>

                        {/* Continue Button */}
                        <div className="text-center form-group mt-4 position-relative">
                          <button
                            className="btn btn-primary btn-block"
                            onClick={handleContinueToStep2}
                            disabled={clickLoading}
                          >
                            <span className="fs-18">
                              {clickLoading ? "Loading..." : "Continue"}
                            </span>
                          </button>
                        </div>
                      </>
                    )}

                    {currentStep === 1 && (
                      <>
                        {/* Step 2 Fields */}
                        <div className={role !== "client" ? "row" : ""}>
                          <div
                            className={`form-group mb-3 ${
                              role !== "client" ? "col-md-6" : ""
                            }`}
                          >
                            <label
                              id="interestFields-label"
                              htmlFor="interestFields"
                              className="form-label font-w600"
                            >
                              Interest Fields
                            </label>
                            <div
                              id="interestFields"
                              name="interestFields"
                              className="form-select form-control d-flex align-items-center mb-1"
                              role="button"
                              tabIndex="0"
                              aria-haspopup="listbox"
                              aria-expanded={openInterestFields}
                              aria-labelledby="interestFields-label"
                              onClick={() =>
                                setOpenInterestFields(!openInterestFields)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setOpenInterestFields(!openInterestFields);
                                }
                              }}
                              style={{
                                cursor: "pointer",
                                position: "relative",
                              }}
                            >
                              {watch("interestFields").length === 0
                                ? "Choose..."
                                : watch("interestFields").join(", ")}

                              {openInterestFields && (
                                <div
                                  className="dropdown-options"
                                  ref={dropdownRef}
                                  style={{
                                    position: "absolute",
                                    top: "calc(100% + 8px)",
                                    left: "2.5%",
                                    right: "0",
                                    width: "95%",
                                    zIndex: 1000,
                                    backgroundColor: "#fff",
                                    border: "1px solid #e6e6e6",
                                    borderRadius: "0.75rem",
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    padding: "1rem",
                                    margin: "0",
                                    scrollBehavior: "smooth",
                                  }}
                                >
                                  {FIELDS_INTEREST.map((field, index) => (
                                    <div
                                      key={index}
                                      className="form-check"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        value={field}
                                        id={`field-${index}`}
                                        name="interestFields"
                                        className="form-check-input"
                                        checked={watch(
                                          "interestFields"
                                        ).includes(field)}
                                        {...register("interestFields")}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <label
                                        htmlFor={`field-${index}`}
                                        className="form-check-label"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {field}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            {errors.interestFields && (
                              <div className="text-danger fs-12">
                                {errors.interestFields.message}
                              </div>
                            )}
                          </div>

                          {role !== "client" && (
                            <div className="form-group mb-3 col-md-6">
                              <label
                                htmlFor="linkedinProfileLink"
                                className="form-label font-w600"
                              >
                                Linkedin Profile
                              </label>
                              <input
                                id="linkedinProfileLink"
                                name="linkedinProfileLink"
                                type="url"
                                className="form-control mb-1"
                                placeholder="Link"
                                autoComplete="url"
                                {...register("linkedinProfileLink")}
                              />
                              {errors.linkedinProfileLink && (
                                <div className="text-danger fs-12">
                                  {errors.linkedinProfileLink.message}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Conditional Fields Based on Role and Study Year */}
                        {role === "contestant" && (
                          <>
                            {/* University and Faculty Fields */}
                            <div className="row">
                              <div className="form-group mb-3 col-md-6">
                                <label
                                  htmlFor="university"
                                  className="form-label font-w600"
                                >
                                  University
                                </label>
                                <input
                                  id="university"
                                  name="university"
                                  type="text"
                                  className="form-control mb-1"
                                  placeholder="Type Your University"
                                  autoComplete="organization"
                                  {...register("university")}
                                />
                                {errors.university && (
                                  <div className="text-danger fs-12">
                                    {errors.university.message}
                                  </div>
                                )}
                              </div>

                              <div className="form-group mb-3 col-md-6">
                                <label
                                  htmlFor="faculty"
                                  className="form-label font-w600"
                                >
                                  College / Faculty
                                </label>
                                <input
                                  id="faculty"
                                  name="faculty"
                                  type="text"
                                  className="form-control mb-1"
                                  placeholder="Type Your Faculty"
                                  autoComplete="off"
                                  {...register("faculty")}
                                />
                                {errors.faculty && (
                                  <div className="text-danger fs-12">
                                    {errors.faculty.message}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="form-group mb-3">
                              <label
                                htmlFor="jobStatus"
                                className="form-label font-w600"
                              >
                                Job Status
                              </label>

                              {/* Hidden input for React Hook Form */}
                              <input
                                type="hidden"
                                {...register("jobStatus")}
                                value={watch("jobStatus") || ""}
                              />

                              {/* Custom dropdown that looks like form-select */}
                              <div
                                className={`form-select form-control solid mb-1 ${
                                  jobStatusDropdownOpen ? "dropdown-open" : ""
                                }`}
                                onClick={() =>
                                  setJobStatusDropdownOpen(
                                    !jobStatusDropdownOpen
                                  )
                                }
                                style={{
                                  cursor: "pointer",
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <div
                                  className="dropdown-selected"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "50%",
                                    height: "100%",
                                  }}
                                >
                                  {watch("jobStatus") || "Choose..."}
                                </div>

                                {jobStatusDropdownOpen && (
                                  <ul
                                    className="dropdown-options"
                                    style={{
                                      position: "absolute",
                                      top: "calc(100% + 8px)",
                                      left: "2.5%",
                                      right: "0",
                                      width: "95%",
                                      zIndex: 1000,
                                      backgroundColor: "#fff",
                                      border: "1px solid #e6e6e6",
                                      borderRadius: "0.75rem",
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                      listStyle: "none",
                                      padding: "0",
                                      margin: "0",
                                    }}
                                  >
                                    {JOB_STATUS.map((status, index) => (
                                      <li
                                        key={index}
                                        className="dropdown-option"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setValue("jobStatus", status);

                                          // Auto-set study year to "Graduate" if job status is not "Student"
                                          if (status !== "Student") {
                                            setValue("studyYear", "Graduate");
                                          }

                                          setJobStatusDropdownOpen(false);
                                        }}
                                        style={{
                                          padding: "0.75rem 1rem",
                                          cursor: "pointer",
                                          fontSize: "0.875rem",
                                          color: "#888888",
                                          margin: "0",
                                          display: "block",
                                          width: "100%",
                                          boxSizing: "border-box",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor =
                                            "#F93A0B";
                                          e.target.style.color = "#fff";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor =
                                            "transparent";
                                          e.target.style.color = "#888888";
                                          e.target.style.padding =
                                            "0.75rem 1rem";
                                          e.target.style.margin = "0";
                                        }}
                                      >
                                        {status}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>

                              {errors.jobStatus && (
                                <div className="text-danger fs-12">
                                  {errors.jobStatus.message}
                                </div>
                              )}
                            </div>

                            {jobStatus === "Student" && (
                              <div className="form-group mb-3">
                                <label
                                  htmlFor="studyYear"
                                  className="form-label font-w600"
                                >
                                  Study Year
                                </label>

                                {/* Hidden input for React Hook Form */}
                                <input
                                  type="hidden"
                                  {...register("studyYear")}
                                  value={watch("studyYear") || ""}
                                />

                                {/* Custom dropdown that looks like form-select */}
                                <div
                                  className={`form-select form-control solid mb-1 ${
                                    studyYearDropdownOpen ? "dropdown-open" : ""
                                  }`}
                                  onClick={() =>
                                    setStudyYearDropdownOpen(
                                      !studyYearDropdownOpen
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  <div
                                    className="dropdown-selected"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "50%",
                                      height: "100%",
                                    }}
                                  >
                                    {watch("studyYear") || "Choose..."}
                                  </div>

                                  {studyYearDropdownOpen && (
                                    <ul
                                      className="dropdown-options"
                                      style={{
                                        position: "absolute",
                                        top: "calc(100% + 8px)",
                                        left: "2.5%",
                                        right: "0",
                                        width: "95%",
                                        zIndex: 1000,
                                        backgroundColor: "#fff",
                                        border: "1px solid #e6e6e6",
                                        borderRadius: "0.75rem",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        listStyle: "none",
                                        padding: "0",
                                        margin: "0",
                                      }}
                                    >
                                      {STUDY_YEARS.map((year, index) => (
                                        <li
                                          key={index}
                                          className="dropdown-option"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setValue("studyYear", year);
                                            setStudyYearDropdownOpen(false);
                                          }}
                                          style={{
                                            padding: "0.75rem 1rem",
                                            cursor: "pointer",
                                            fontSize: "0.875rem",
                                            color: "#888888",
                                            margin: "0",
                                            display: "block",
                                            width: "100%",
                                            boxSizing: "border-box",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor =
                                              "#F93A0B";
                                            e.target.style.color = "#fff";
                                            e.target.style.padding =
                                              "0.75rem 1rem";
                                            e.target.style.margin = "0";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor =
                                              "transparent";
                                            e.target.style.color = "#888888";
                                            e.target.style.padding =
                                              "0.75rem 1rem";
                                            e.target.style.margin = "0";
                                          }}
                                        >
                                          {year}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>

                                {errors.studyYear && (
                                  <div className="text-danger fs-12">
                                    {errors.studyYear.message}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        {/* University and Faculty Fields - Always show for non-contestants */}
                        {role !== "contestant" && (
                          <div className="row">
                            <div className="form-group mb-3 col-md-6">
                              <label
                                htmlFor="university"
                                className="form-label font-w600"
                              >
                                University
                              </label>
                              <input
                                id="university"
                                name="university"
                                type="text"
                                className="form-control mb-1"
                                placeholder="Type Your University"
                                autoComplete="organization"
                                {...register("university")}
                              />
                              {errors.university && (
                                <div className="text-danger fs-12">
                                  {errors.university.message}
                                </div>
                              )}
                            </div>

                            <div className="form-group mb-3 col-md-6">
                              <label
                                htmlFor="faculty"
                                className="form-label font-w600"
                              >
                                Faculty
                              </label>
                              <input
                                id="faculty"
                                name="faculty"
                                type="text"
                                className="form-control mb-1"
                                placeholder="Type Your Faculty"
                                autoComplete="off"
                                {...register("faculty")}
                              />
                              {errors.faculty && (
                                <div className="text-danger fs-12">
                                  {errors.faculty.message}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Experience Fields Based on Job Status - Only for Contestants */}
                        {role === "contestant" &&
                          jobStatus &&
                          jobStatus !== "Student" && (
                            <>
                              {/* For Employed (full-time or part-time) - Show Company Name and Years of Experience side by side */}
                              {(jobStatus === "Employed full-time" ||
                                jobStatus === "Employed part-time") && (
                                <div className="row">
                                  <div className="form-group mb-2 col-sm-6">
                                    <label
                                      htmlFor="companyName"
                                      className="form-label font-w600"
                                    >
                                      Company Name
                                    </label>
                                    <input
                                      id="companyName"
                                      name="companyName"
                                      type="text"
                                      className="form-control mb-1"
                                      placeholder="Type Your Company"
                                      autoComplete="organization"
                                      {...register("companyName")}
                                    />
                                    {errors.companyName && (
                                      <div className="text-danger fs-12">
                                        {errors.companyName.message}
                                      </div>
                                    )}
                                  </div>

                                  <div className="form-group mb-2 col-sm-6">
                                    <label
                                      htmlFor="experienceYears"
                                      className="form-label font-w600"
                                    >
                                      Years of experience in your field
                                    </label>

                                    {/* Hidden input for form registration */}
                                    <input
                                      type="hidden"
                                      {...register("experienceYears")}
                                      value={watch("experienceYears") || ""}
                                    />

                                    {/* Custom dropdown */}
                                    <div
                                      className={`form-select form-control solid mb-1 ${
                                        experienceYearsDropdownOpen
                                          ? "dropdown-open"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setExperienceYearsDropdownOpen(
                                          !experienceYearsDropdownOpen
                                        )
                                      }
                                      style={{
                                        cursor: "pointer",
                                        position: "relative",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <div
                                        className="dropdown-selected"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          width: "50%",
                                          height: "100%",
                                        }}
                                      >
                                        {watch("experienceYears")
                                          ? `${watch("experienceYears")} year${
                                              watch("experienceYears") === "1"
                                                ? ""
                                                : "s"
                                            }`
                                          : "Choose..."}
                                      </div>

                                      {experienceYearsDropdownOpen && (
                                        <ul
                                          className="dropdown-options"
                                          style={{
                                            position: "absolute",
                                            top: "calc(100% + 8px)",
                                            left: "2.5%",
                                            right: "0",
                                            width: "95%",
                                            zIndex: 1000,
                                            backgroundColor: "#fff",
                                            border: "1px solid #e6e6e6",
                                            borderRadius: "0.75rem",
                                            maxHeight: "180px",
                                            overflowY: "auto",
                                            listStyle: "none",
                                            padding: "0",
                                            margin: "0",
                                          }}
                                        >
                                          {EXPERIENCE_LEVELS.map((year) => (
                                            <li
                                              key={year}
                                              className="dropdown-option"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setValue(
                                                  "experienceYears",
                                                  year.toString()
                                                );
                                                setExperienceYearsDropdownOpen(
                                                  false
                                                );
                                              }}
                                              style={{
                                                padding: "0.5rem 0.75rem",
                                                cursor: "pointer",
                                                fontSize: "0.875rem",
                                                color: "#888888",
                                                margin: "0",
                                                display: "block",
                                                width: "100%",
                                                boxSizing: "border-box",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.backgroundColor =
                                                  "#F93A0B";
                                                e.target.style.color = "#fff";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.backgroundColor =
                                                  "transparent";
                                                e.target.style.color =
                                                  "#888888";
                                              }}
                                            >
                                              {year} year{year === 1 ? "" : "s"}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>

                                    {errors.experienceYears && (
                                      <div className="text-danger fs-12">
                                        {errors.experienceYears.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* For Currently not working or Freelancer - show Years of Experience full width */}
                              {(jobStatus === "Currently not working" ||
                                jobStatus === "Freelancer") && (
                                <div className="form-group mb-2">
                                  <label
                                    htmlFor="experienceYears"
                                    className="form-label font-w600"
                                  >
                                    Years of experience in field
                                  </label>

                                  {/* Hidden input for form registration */}
                                  <input
                                    type="hidden"
                                    {...register("experienceYears")}
                                    value={watch("experienceYears") || ""}
                                  />

                                  {/* Custom dropdown */}
                                  <div
                                    className={`form-select form-control solid mb-1 ${
                                      experienceYearsDropdownOpen
                                        ? "dropdown-open"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setExperienceYearsDropdownOpen(
                                        !experienceYearsDropdownOpen
                                      )
                                    }
                                    style={{
                                      cursor: "pointer",
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                    }}
                                  >
                                    <div
                                      className="dropdown-selected"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "50%",
                                        height: "100%",
                                      }}
                                    >
                                      {watch("experienceYears")
                                        ? `${watch("experienceYears")} year${
                                            watch("experienceYears") === "1"
                                              ? ""
                                              : "s"
                                          }`
                                        : "Choose..."}
                                    </div>

                                    {experienceYearsDropdownOpen && (
                                      <ul
                                        className="dropdown-options"
                                        style={{
                                          position: "absolute",
                                          top: "calc(100% + 8px)",
                                          left: "2.5%",
                                          right: "0",
                                          width: "95%",
                                          zIndex: 1000,
                                          backgroundColor: "#fff",
                                          border: "1px solid #e6e6e6",
                                          borderRadius: "0.75rem",
                                          maxHeight: "180px",
                                          overflowY: "auto",
                                          listStyle: "none",
                                          padding: "0",
                                          margin: "0",
                                        }}
                                      >
                                        {EXPERIENCE_LEVELS.map((year) => (
                                          <li
                                            key={year}
                                            className="dropdown-option"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setValue(
                                                "experienceYears",
                                                year.toString()
                                              );
                                              setExperienceYearsDropdownOpen(
                                                false
                                              );
                                            }}
                                            style={{
                                              padding: "0.5rem 0.75rem",
                                              cursor: "pointer",
                                              fontSize: "0.875rem",
                                              color: "#888888",
                                              margin: "0",
                                              display: "block",
                                              width: "100%",
                                              boxSizing: "border-box",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.backgroundColor =
                                                "#F93A0B";
                                              e.target.style.color = "#fff";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.backgroundColor =
                                                "transparent";
                                              e.target.style.color = "#888888";
                                            }}
                                          >
                                            {year} year{year === 1 ? "" : "s"}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>

                                  {errors.experienceYears && (
                                    <div className="text-danger fs-12">
                                      {errors.experienceYears.message}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        {/* Experience Fields - For Non-Contestants */}
                        {role !== "contestant" && (
                          <div className={role !== "client" ? "row" : ""}>
                            <div
                              className={`form-group mb-2 ${
                                role !== "client" ? "col-md-4" : ""
                              }`}
                            >
                              <label
                                htmlFor="companyName"
                                className="form-label font-w600"
                              >
                                Company Name
                              </label>
                              <input
                                id="companyName"
                                name="companyName"
                                type="text"
                                className="form-control mb-1"
                                placeholder="Type Your Company"
                                autoComplete="organization"
                                {...register("companyName")}
                              />
                              {errors.companyName && (
                                <div className="text-danger fs-12">
                                  {errors.companyName.message}
                                </div>
                              )}
                            </div>

                            {role !== "client" && (
                              <>
                                <div className="form-group mb-2 col-md-4">
                                  <label
                                    htmlFor="experienceYears"
                                    className="form-label font-w600"
                                  >
                                    Experience Years
                                  </label>

                                  {/* Hidden input for form registration */}
                                  <input
                                    type="hidden"
                                    {...register("experienceYears")}
                                    value={watch("experienceYears") || ""}
                                  />

                                  {/* Custom dropdown */}
                                  <div
                                    className={`form-select form-control solid mb-1 ${
                                      experienceYearsDropdownOpen
                                        ? "dropdown-open"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setExperienceYearsDropdownOpen(
                                        !experienceYearsDropdownOpen
                                      )
                                    }
                                    style={{
                                      cursor: "pointer",
                                      position: "relative",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                    }}
                                  >
                                    <div
                                      className="dropdown-selected"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "50%",
                                        height: "100%",
                                      }}
                                    >
                                      {watch("experienceYears")
                                        ? `${watch("experienceYears")} year${
                                            watch("experienceYears") === "1"
                                              ? ""
                                              : "s"
                                          }`
                                        : "Choose..."}
                                    </div>

                                    {experienceYearsDropdownOpen && (
                                      <ul
                                        className="dropdown-options"
                                        style={{
                                          position: "absolute",
                                          top: "calc(100% + 8px)",
                                          left: "2.5%",
                                          right: "0",
                                          width: "95%",
                                          zIndex: 1000,
                                          backgroundColor: "#fff",
                                          border: "1px solid #e6e6e6",
                                          borderRadius: "0.75rem",
                                          maxHeight: "180px",
                                          overflowY: "auto",
                                          listStyle: "none",
                                          padding: "0",
                                          margin: "0",
                                        }}
                                      >
                                        {EXPERIENCE_LEVELS.map((year) => (
                                          <li
                                            key={year}
                                            className="dropdown-option"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setValue(
                                                "experienceYears",
                                                year.toString()
                                              );
                                              setExperienceYearsDropdownOpen(
                                                false
                                              );
                                            }}
                                            style={{
                                              padding: "0.5rem 0.75rem",
                                              cursor: "pointer",
                                              fontSize: "0.875rem",
                                              color: "#888888",
                                              margin: "0",
                                              display: "block",
                                              width: "100%",
                                              boxSizing: "border-box",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.backgroundColor =
                                                "#F93A0B";
                                              e.target.style.color = "#fff";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.backgroundColor =
                                                "transparent";
                                              e.target.style.color = "#888888";
                                            }}
                                          >
                                            {year} year{year === 1 ? "" : "s"}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>

                                  {errors.experienceYears && (
                                    <div className="text-danger fs-12">
                                      {errors.experienceYears.message}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}{" "}
                        {/* Where You Know Us */}
                        <div className="form-group mb-3">
                          <label
                            id="whereYouKnowUs-label"
                            htmlFor="whereYouKnowUs"
                            className="form-label font-w600"
                          >
                            Where You Know Us
                          </label>
                          <div
                            id="whereYouKnowUs"
                            name="whereYouKnowUs"
                            className="form-select form-control d-flex align-items-center mb-1"
                            role="button"
                            tabIndex="0"
                            aria-haspopup="listbox"
                            aria-expanded={openAwarenessChannels}
                            aria-labelledby="whereYouKnowUs-label"
                            onClick={() =>
                              setOpenAwarenessChannels(!openAwarenessChannels)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setOpenAwarenessChannels(
                                  !openAwarenessChannels
                                );
                              }
                            }}
                            style={{
                              cursor: "pointer",
                              position: "relative",
                            }}
                          >
                            {whereYouKnowUs.length === 0
                              ? "Choose..."
                              : whereYouKnowUs.join(", ")}

                            {openAwarenessChannels && (
                              <div
                                className="dropdown-options"
                                ref={awarenessChannelsRef}
                                style={{
                                  position: "absolute",
                                  top: "calc(100% + 8px)",
                                  left: "2.5%",
                                  right: "0",
                                  width: "95%",
                                  zIndex: 1000,
                                  backgroundColor: "#fff",
                                  border: "1px solid #e6e6e6",
                                  borderRadius: "0.75rem",
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                  padding: "1rem",
                                  margin: "0",
                                  scrollBehavior: "smooth",
                                }}
                              >
                                {AWARENESS_CHANNELS.map((channel, index) => (
                                  <div
                                    key={index}
                                    className="form-check"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      value={channel}
                                      id={`channel-${index}`}
                                      name="where_you_know_us"
                                      className="form-check-input"
                                      checked={whereYouKnowUs?.includes(
                                        channel
                                      )}
                                      onChange={handleWhereYouKnowUsChange}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <label
                                      htmlFor={`channel-${index}`}
                                      className="form-check-label"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {channel}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          {errors.where_you_know_us && (
                            <div className="text-danger fs-12">
                              {errors.where_you_know_us.message}
                            </div>
                          )}
                        </div>
                        {/* Other Awareness Channel */}
                        {isOtherSelected && (
                          <div className="form-group mb-3">
                            <label
                              htmlFor="otherAwarenessChannel"
                              className="form-label font-w600"
                            >
                              Other{" "}
                              <span className="text-danger scale5 ms-2">*</span>
                            </label>
                            <input
                              id="otherAwarenessChannel"
                              name="otherAwarenessChannel"
                              type="text"
                              className="form-control mb-1"
                              placeholder="Other"
                              autoComplete="off"
                              {...register("otherAwarenessChannel")}
                            />
                            {errors.otherAwarenessChannel && (
                              <div className="text-danger fs-12">
                                {errors.otherAwarenessChannel.message}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Country and City Fields */}
                        <div className="row">
                          <div className="form-group mb-3 col-md-6">
                            <label
                              htmlFor="country"
                              className="form-label font-w600"
                            >
                              Country
                            </label>
                            <input
                              id="country"
                              name="country"
                              type="text"
                              className="form-control mb-1 r"
                              placeholder="Type Your Country"
                              autoComplete="country-name"
                              {...register("country")}
                            />
                            {errors.country && (
                              <div className="text-danger fs-12">
                                {errors.country.message}
                              </div>
                            )}
                          </div>
                          <div className="form-group mb-3 col-md-6">
                            <label
                              htmlFor="city"
                              className="form-label font-w600"
                            >
                              City
                            </label>
                            <input
                              id="city"
                              name="city"
                              type="text"
                              className="form-control mb-1"
                              placeholder="Type Your City"
                              autoComplete="address-level2"
                              {...register("city")}
                            />
                            {errors.city && (
                              <div className="text-danger fs-12">
                                {errors.city.message}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Skip and Continue Buttons */}
                        <div className="row">
                          <div className="form-group mt-4 col-md-6">
                            <button
                              className="btn btn-outline-primary btn-block position-relative overflow-hidden"
                              onClick={handleSkipStep2}
                              disabled={clickLoading}
                            >
                              {clickLoading ? (
                                <>
                                  <span>Loading</span>
                                  <span
                                    className="spinner-grow spinner-grow-sm"
                                    style={{ marginLeft: "5px" }}
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                </>
                              ) : (
                                "Skip"
                              )}
                            </button>
                          </div>

                          <div className="form-group mt-4 col-md-6">
                            <button
                              className="btn btn-primary btn-block"
                              onClick={handleSubmit(onSubmit)}
                              disabled={clickLoading}
                            >
                              {clickLoading ? (
                                <>
                                  <span className="fs-15">Loading</span>
                                  <span
                                    className="spinner-grow spinner-grow-sm"
                                    style={{ marginLeft: "5px" }}
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                </>
                              ) : (
                                <span className="fs-15">Continue</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default ProfileData;
